import useUrlParamState from "hooks/useUrlParamState";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnack } from "hooks/useSnack";
import { ChargerEstimatorPages } from "./chargerEstimatorTools";
import { ProgressSteps } from "components/basic/progressSteps";
import { MeQuery } from "generated/graphql";
import { track } from "analytics";
import {
  estimatorDraftKey,
  loadEstimatorDraft,
  saveEstimatorDraft,
} from "./helpers/estimatorDraft";
import { getRecoveryRedirect } from "./helpers/getRecoveryRedirect";
import { QuoteNavigationState } from "./formPages/quote/quoteTools";

export interface ChargerEstimatorData {
  address: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;
  installationType: string;
  vehicleType: string;
  houseType: string;
  chargerLocation: string;
  chargerSetup: string;
  electricalPanelLocation: string;
  mainServiceSize: string;
  electricalPanelPhoto?: File | null;
  electricalPanelPhotoURL?: string;
  mainBreakerPhoto?: File | null;
  mainBreakerPhotoURL?: string;
  wireLength: string;
  purchaseType: string;
  elevator: boolean | undefined;
  pool: boolean | undefined;
  hotTub: boolean | undefined;
  fullName: string;
  email: string;
  phoneNumber: string;
  additionalInformation?: string;
}

export interface ChargerEstimatorToolsPagesProps {
  chargerEstimatorData: ChargerEstimatorData;
  setChargerEstimatorData: React.Dispatch<
    React.SetStateAction<ChargerEstimatorData>
  >;
}

interface ChargerEstimatorProps {
  account?: MeQuery["me"];
}

const ChargerEstimator: React.FC<ChargerEstimatorProps> = ({ account }) => {
  const { t } = useTranslation("estimator");
  const navigate = useNavigate();
  const location = useLocation();
  const showSnack = useSnack();
  const [currentPage] = useUrlParamState("currentPage", "addressPage");
  const draftKey = estimatorDraftKey();

  const [chargerEstimatorData, setChargerEstimatorData] =
    useState<ChargerEstimatorData>(() => ({
      installationType: "",
      vehicleType: "",
      houseType: "",
      chargerLocation: "",
      chargerSetup: "",
      electricalPanelLocation: "",
      mainServiceSize: "",
      wireLength: "",
      purchaseType: "",
      elevator: undefined,
      pool: undefined,
      hotTub: undefined,
      ...(account
        ? {
            fullName: `${account.firstName} ${account.lastName}`,
            email: account.email,
            phoneNumber: account?.phoneNumber || "",
            // Seed the search origin from the user's saved address; the
            // estimator address page can still override it per search.
            address: account.address || "",
            latitude: account.latitude ?? undefined,
            longitude: account.longitude ?? undefined,
            placeId: account.placeId ?? undefined,
          }
        : {
            fullName: "",
            email: "",
            phoneNumber: "",
            address: "",
          }),
      ...loadEstimatorDraft(draftKey),
    }));

  useEffect(() => {
    saveEstimatorDraft(draftKey, chargerEstimatorData);
  }, [draftKey, chargerEstimatorData]);

  const accountSeeded = useRef(false);
  useEffect(() => {
    if (!account || accountSeeded.current) {
      return;
    }
    accountSeeded.current = true;

    setChargerEstimatorData((previous) => ({
      ...previous,
      fullName: previous.fullName || `${account.firstName} ${account.lastName}`,
      email: previous.email || account.email,
      phoneNumber: previous.phoneNumber || account.phoneNumber || "",
      ...(previous.address
        ? {}
        : {
            address: account.address || "",
            latitude: account.latitude ?? undefined,
            longitude: account.longitude ?? undefined,
            placeId: account.placeId ?? undefined,
          }),
    }));
  }, [account]);

  const latestData = useRef(chargerEstimatorData);
  latestData.current = chargerEstimatorData;
  const hasQuoteState = Boolean(
    (location.state as QuoteNavigationState | null)?.service,
  );
  const recovery = useMemo(
    () =>
      getRecoveryRedirect(
        currentPage,
        latestData.current,
        hasQuoteState,
        "selectEstimate",
      ),
    [currentPage, hasQuoteState],
  );

  useEffect(() => {
    if (!recovery) {
      return;
    }

    showSnack(recovery.message, "warning");
    navigate(recovery.search, { replace: true });
  }, [recovery, navigate, showSnack]);

  // The estimator advances by querystring, not by route, so the pathname-keyed
  // pageview hook never sees a step change. This is what produces the drop-off
  // curve.
  useEffect(() => {
    if (recovery) {
      return;
    }
    track("estimator_step", {
      step: currentPage,
      index: ChargerEstimatorPages.findIndex(
        (page) => page.pageName === currentPage,
      ),
    });
  }, [currentPage, recovery]);

  const currentPageComponent = useMemo(() => {
    return ChargerEstimatorPages.find((page) => page.pageName === currentPage);
  }, [currentPage]);

  const progressStepsComponent = useMemo(() => {
    const pages = ["vehicleType", "homeSetup", "powerAccess", "preferences"];

    if (!pages.includes(currentPage)) {
      return null;
    }

    const currentStep = pages.indexOf(currentPage);
    const rightAlignProgressBar = ["homeSetup", "preferences"].includes(
      currentPage,
    );

    return (
      <ProgressSteps
        steps={[
          { label: t("steps.vehicle") },
          { label: t("steps.home") },
          { label: t("steps.power") },
          { label: t("steps.preferences") },
        ]}
        currentStep={currentStep}
        sx={{
          // On desktop the bar is pinned under whichever half holds the form.
          // Below md the photo is hidden and the page scrolls, so it sits in
          // normal flow directly after the form instead.
          position: { xs: "static", md: "absolute" },
          bottom: { md: 0 },
          left: { md: rightAlignProgressBar ? "50%" : 0 },
          width: { xs: "100%", md: "50%" },
          px: { xs: 2, sm: 3, md: 5 },
          pt: 0,
          pb: { xs: 3, md: 5 },
        }}
      />
    );
  }, [currentPage, t]);

  if (recovery) {
    return null;
  }

  return (
    <>
      {currentPageComponent?.component(
        chargerEstimatorData,
        setChargerEstimatorData,
      )}

      {progressStepsComponent}
    </>
  );
};

export default ChargerEstimator;
